import { useColorMode } from '@euler/app/theming/color-mode';
import { StyleProps, useViewStyleProps } from '@euler/components/layout/View';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo, useMemo } from 'react';
import {
  Text as NativeText,
  StyleProp,
  StyleSheet,
  TextProps,
  TextStyle,
} from 'react-native';
import { DefaultTheme, useTheme } from 'styled-components';
import { ParagraphClamped, ParagraphClampedProps } from './ParagraphClamped';

export namespace Typography {
  type FontFamilyType =
    | 'thin'
    | 'light'
    | 'regular'
    | 'medium'
    | 'bold'
    | 'black';

  type TitleLevel = 1 | 2 | 3 | 4 | 5;

  const TitleLevel2FontSizeMap: { [p in TitleLevel]: number } = {
    1: 19,
    2: 17,
    3: 15,
    4: 13,
    5: 11,
  };

  type DistributiveMap<T extends string> = T extends unknown
    ? {
        [p in T]?: true;
      }
    : never;

  type FontFamilyShortcut = DistributiveMap<FontFamilyType>;

  const FontFamilyMap: {
    [p in FontFamilyType]: { name: string; weight: number };
  } = {
    thin: { name: FontFamily.NotoSans.Thin, weight: 100 },
    light: { name: FontFamily.NotoSans.Light, weight: 300 },
    regular: { name: FontFamily.NotoSans.Regular, weight: 400 },
    medium: { name: FontFamily.NotoSans.Medium, weight: 500 },
    bold: { name: FontFamily.NotoSans.Bold, weight: 700 },
    black: { name: FontFamily.NotoSans.Black, weight: 900 },
  };

  const kFontFamilyShortcut = Object.keys(FontFamilyMap) as FontFamilyType[];

  // Reverse lookup: a NotoSans font-family NAME → its numeric weight. Used
  // to recover the right `fontWeight` when a caller sets the weight via
  // `style.fontFamily` (the legacy pattern) instead of the `family`/`bold`
  // props.
  const kFamilyNameToWeight: { [name: string]: number } = Object.fromEntries(
    kFontFamilyShortcut.map(k => [
      FontFamilyMap[k].name,
      FontFamilyMap[k].weight,
    ]),
  );

  const hasFamilyProp = (props: any) =>
    kFontFamilyShortcut.some(x => x in props);

  export type TextBaseProps = TextProps & {
    family?: FontFamilyType;
    size?: number;
    color?: string | ((theme: DefaultTheme) => string);
    lineHeight?: number | 'auto';
    align?: TextStyle['textAlign'];
    textTransform?: TextStyle['textTransform'];
    letterSpacing?: TextStyle['letterSpacing'];
    decorationLine?: TextStyle['textDecorationLine'];
    decorationStyle?: TextStyle['textDecorationStyle'];
    decorationColor?: TextStyle['textDecorationColor'];
    textShadowColor?: TextStyle['textShadowColor'];
    textShadowOffset?: TextStyle['textShadowOffset'];
    textShadowRadius?: TextStyle['textShadowRadius'];
  } & FontFamilyShortcut &
    StyleProps;

  const useTextBaseStyle = ({
    family,
    size,
    color,
    align,
    textTransform,
    style,
    lineHeight,
    letterSpacing,
    decorationLine: textDecorationLine,
    decorationStyle: textDecorationStyle,
    decorationColor: textDecorationColor,
    textShadowColor,
    textShadowOffset,
    textShadowRadius,
    ...props
  }: TextBaseProps) => {
    const theme = useTheme();
    const isDarkMode = useColorMode() === 'dark';
    const { rpx } = useResponsiveContext();

    const [viewStyle, viewProps] = useViewStyleProps(props);

    size = size ?? rpx(14);

    if (!family) {
      for (const p of kFontFamilyShortcut) {
        if (p in viewProps && (viewProps as any)[p]) {
          family = p;
          break;
        }
      }
    }

    const fontFamily = family ? FontFamilyMap[family] : FontFamilyMap.regular;

    // Legacy callers set the weight via `style.fontFamily` (e.g.
    // `FontFamily.NotoSans.Bold`) but leave the computed `fontWeight: 400`.
    // The web resolves the bold face by name, but native needs family +
    // weight to AGREE — a weight-specific family fighting a 400 weight
    // falls back to regular. Recover the matching weight from the style's
    // fontFamily and append it last so the two line up on device.
    const styleFontFamily = (StyleSheet.flatten(style) as TextStyle | undefined)
      ?.fontFamily;
    const styleWeightFix =
      styleFontFamily != null && kFamilyNameToWeight[styleFontFamily] != null
        ? String(kFamilyNameToWeight[styleFontFamily])
        : undefined;

    const textColor =
      typeof color === 'function'
        ? color(theme)
        : color ?? (isDarkMode ? 'white' : 'black');

    const styles = useMemo<StyleProp<TextStyle>>(() => {
      return [
        viewStyle,
        {
          textAlign: align,
          fontFamily: fontFamily.name,
          fontWeight: String(fontFamily.weight) as any,
          textTransform,
          fontSize: size,
          lineHeight: lineHeight === 'auto' ? undefined : lineHeight,
          letterSpacing,
          color: textColor,
          textDecorationLine,
          textDecorationStyle,
          textDecorationColor,
          textShadowColor,
          textShadowOffset,
          textShadowRadius,
        },
        style,
        styleWeightFix
          ? { fontWeight: styleWeightFix as TextStyle['fontWeight'] }
          : null,
      ];
    }, [
      viewStyle,
      align,
      fontFamily.name,
      fontFamily.weight,
      textTransform,
      size,
      lineHeight,
      letterSpacing,
      textColor,
      textDecorationLine,
      textDecorationStyle,
      textDecorationColor,
      textShadowColor,
      textShadowOffset,
      textShadowRadius,
      style,
      styleWeightFix,
    ]);

    return [styles, viewProps] as const;
  };

  const TextBase: FC<TextBaseProps> = memo(props => {
    const [styles, { children, ...textProps }] = useTextBaseStyle(props);

    return (
      <NativeText style={styles} {...textProps}>
        {children}
      </NativeText>
    );
  });

  export const Title: FC<TextBaseProps & { level?: TitleLevel }> = memo(
    ({ level = 4, ...props }) => {
      const { rpx } = useResponsiveContext();
      return (
        <TextBase
          size={rpx(TitleLevel2FontSizeMap[level])}
          bold={!hasFamilyProp(props) as any}
          {...props}
        />
      );
    },
  );

  export const Text: FC<TextBaseProps> = memo(props => {
    return <TextBase {...props} />;
  });

  export const Paragraph: FC<
    TextBaseProps & {
      ellipsis?: ParagraphClampedProps;
    }
  > = memo(({ ellipsis, size, lineHeight, ...props }) => {
    const { rpx } = useResponsiveContext();
    size ??= rpx(12);
    lineHeight ??= rpx(18);

    const [style, { children }] = useTextBaseStyle({
      ...props,
      size,
      lineHeight,
    });

    if (ellipsis?.numberOfLines != null && ellipsis?.numberOfLines > 0) {
      return (
        <ParagraphClamped {...ellipsis} textStyle={style}>
          {children}
        </ParagraphClamped>
      );
    }
    return (
      <TextBase style={style} size={size} lineHeight={lineHeight} {...props} />
    );
  });

  export const Label: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size = size ?? rpx(16);
      lineHeight = lineHeight ?? Math.floor(size! * 1.6);

      return <TextBase size={size} lineHeight={lineHeight} {...props} />;
    },
  );

  export const Body: FC<TextBaseProps> = memo(
    ({ size, lineHeight, ...props }) => {
      const { rpx } = useResponsiveContext();
      size ??= rpx(14);
      lineHeight ??= rpx(21);

      return <TextBase size={size} lineHeight={lineHeight} {...props} />;
    },
  );
}
